import {
  type SharedFolderActorRef,
  type SharedFolderDirectoryEntry,
  type SharedFolderFileEntry,
} from '../crypto/sharedFolderManifest';
import { fileTypeLabelKeyFor } from '../files/fileTypes';
import { applyI18n, t } from '../i18n';
import { formatFileSize } from '../crypto/sharedFolderFile';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from './modalDialogShell';

function actorLabel(actor: SharedFolderActorRef | undefined): string {
  return actor?.displayName?.trim() || t('files.owner.unknownUploader');
}

export function showFilesPropertiesDialog(input: {
  entry: SharedFolderDirectoryEntry | SharedFolderFileEntry;
  currentPath: string;
}): void {
  const shell = createModalShell({
    titleKey: 'files.owner.properties',
    helpTopic: dialogHelpTopic('filesProperties'),
    className: 'files-properties-dialog',
  });

  const fileName = input.entry.path.split('/').pop() ?? input.entry.path;
  const typeLabel = input.entry.type === 'file'
    ? t(fileTypeLabelKeyFor(fileName, input.entry.contentType))
    : t('files.owner.folderType');
  const sizeMarkup = input.entry.type === 'file'
    ? `<dt data-i18n="files.owner.fileSize"></dt><dd>${escapeHtml(formatFileSize(input.entry.size))}</dd>
      <dt data-i18n="files.owner.fileSizeExact"></dt><dd>${escapeHtml(t('files.owner.fileSizeBytes', { size: input.entry.size.toLocaleString() }))}</dd>`
    : '';
  const createdAt = input.entry.type === 'file'
    ? (input.entry.createdAt ?? input.entry.updatedAt)
    : input.entry.createdAt;
  const createdBy = input.entry.createdBy;
  const updatedAt = input.entry.type === 'file' ? input.entry.updatedAt : input.entry.createdAt;
  const updatedBy = input.entry.type === 'file'
    ? (input.entry.updatedBy ?? input.entry.createdBy)
    : input.entry.createdBy;

  shell.body.innerHTML = `
    <dl class="files-properties-dialog__grid">
      <dt data-i18n="files.owner.fileName"></dt>
      <dd>${escapeHtml(fileName)}</dd>
      <dt data-i18n="files.owner.propertiesType"></dt>
      <dd>${escapeHtml(typeLabel)}</dd>
      <dt data-i18n="files.owner.propertiesLocation"></dt>
      <dd>${escapeHtml(input.currentPath || '/')}</dd>
      ${sizeMarkup}
      <dt data-i18n="files.owner.propertiesCreatedAt"></dt>
      <dd>${escapeHtml(formatDate(createdAt))}</dd>
      <dt data-i18n="files.owner.propertiesCreatedBy"></dt>
      <dd>${escapeHtml(actorLabel(createdBy))}</dd>
      <dt data-i18n="files.owner.propertiesUpdatedAt"></dt>
      <dd>${escapeHtml(formatDate(updatedAt))}</dd>
      <dt data-i18n="files.owner.propertiesUpdatedBy"></dt>
      <dd>${escapeHtml(actorLabel(updatedBy))}</dd>
    </dl>
  `;

  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const close = () => {
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });
}

function formatDate(value: string): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
